import React, { useState } from 'react';
import { StepContainer } from '../components';
import { StepProps, useFormContext } from '../context';
import { CheckCircle, Heart, Instagram, MessageCircleHeart } from 'lucide-react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const Step23_Finalizacao: React.FC<StepProps> = ({ onNext, onPrevious }) => {
  const { data } = useFormContext();
  const [isAllDataCollapsed, setIsAllDataCollapsed] = useState(true);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    basicos: true,
    endereco: true,
    referencias: true,
    financeiro: true,
    motivos: true,
    tecnologia: true,
    contrato: true,
    duvidas: true,
  });

  const toggleAllData = () => {
    setIsAllDataCollapsed(!isAllDataCollapsed);
  };

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatIncomeLabel = (income: string): string => {
    const incomeMap: Record<string, string> = {
      'up-to-1000': 'Até R$ 1.000',
      '1000-2000': 'R$ 1.000 - R$ 2.000',
      '2000-3000': 'R$ 2.000 - R$ 3.000',
      '3000-5000': 'R$ 3.000 - R$ 5.000',
      '5000-10000': 'R$ 5.000 - R$ 10.000',
      'above-10000': 'Acima de R$ 10.000'
    };
    return incomeMap[income] || income;
  };

  const dataSections = [   
    {
      id: 'basicos',
      title: '👤 Dados Básicos',
      items: [
        { label: 'Nome Completo', value: data.fullName },
        { label: 'Email', value: data.email },
        { label: 'Telefone', value: data.phone },
        { label: 'Data de Nascimento', value: data.dateOfBirth },
        { label: 'CPF', value: data.cpfDocument },
        { label: 'RG', value: data.rgDocument },
        { label: 'Nacionalidade', value: data.nationality },
      ]
    },
    {
      id: 'endereco',
      title: '🏠 Endereço',
      items: [
        { label: 'CEP', value: data.cep },
        { label: 'Endereço', value: data.address },
        { label: 'Número', value: data.addressNumber },
        { label: 'Complemento', value: data.complement || '-' },
        { label: 'Bairro', value: data.neighborhood },
        { label: 'Cidade/Estado', value: data.cityState },
        { label: 'Tipo de Residência', value: data.residence },
        { label: 'Tempo na Residência', value: data.residenceTime },
        { label: 'Comprovante de Residência', value: data.residenceProof },
      ]
    },
    {
      id: 'financeiro',
      title: '💰 Financeiro',
      items: [
        { label: 'Profissão', value: data.job },
        { label: 'Renda', value: formatIncomeLabel(data.income) },
        { label: 'Objetivo de Lucro', value: data.profitGoal },
        { label: 'Cartão de Crédito', value: data.creditCard === 'Sim' ? 'Sim' : data.creditCard === 'Não' ? 'Não' : data.creditCard },
        { label: 'Veículo', value: data.veiculo },
      ]
    },
    {
      id: 'pessoal',
      title: '👨‍👩‍👧‍👦 Informações Pessoais',
      items: [
        { label: 'Estado Civil', value: data.maritalStatus },
        { label: 'Nome da Mãe', value: data.mothersName },
        { label: 'Filhos', value: data.children },
        { label: 'Nível Educacional', value: data.scholarLevel },
      ]
    },
    {
      id: 'referencias',
      title: '🤝 Referências',
      items: [
        { label: '1ª Referência Familiar', value: data.referenceNameOne ? `${data.referenceNameOne} - ${data.referencePhoneOne}` : '-' },
        { label: '2ª Referência Familiar', value: data.referenceNameTwo ? `${data.referenceNameTwo} - ${data.referencePhoneTwo}` : '-' },
        { label: 'Referência Comercial', value: data.referenceComercial ? `${data.referenceComercial} - ${data.referenceComercialPhone}` : '-' },
      ]
    },
    {
      id: 'motivos',
      title: '💭 Motivações e Razões',
      items: [
        { label: 'Objetivo de Renda', value: data.salesMotivation || '-' },
        { label: 'Por que ser Embaixadora?', value: data.ambassadorReason || '-' },
      ]
    },
    {
      id: 'tecnologia',
      title: '📱 Tecnologia e Interesse',
      items: [
        { label: 'Interesse em Xizinha', value: data.xizinhaInterest || '-' },
        { label: 'Aprendizado de Apps', value: data.appLearning || '-' },
        { label: 'Confiança em Tecnologia', value: data.technologyConfidence || '-' },
        { label: 'Disponibilidade para Entrevista', value: data.disponivelEntrevistaOnline || '-' },
        { label: 'Disponibilidade para Treinamento', value: data.disponivelTreinamentoVendas || '-' },
      ]
    },
    {
      id: 'contrato',
      title: '📝 Acordo de Contrato',
      items: [
        { label: 'Concordância com Contrato de Responsabilidade', value: data.contractAgreement || '-' },
      ]
    },
    {
      id: 'duvidas',
      title: '❓ Dúvidas',
      items: [
        { label: 'Dúvidas', value: data.doubtsQuestion || '-' },
      ]
    },
    {
      id: 'social',
      title: '📸 Redes Sociais',
      items: [
        { label: 'Instagram', value: data.instagram || '-' },
      ]
    },
  ];

  return (
    <StepContainer title="">
      <div className="space-y-4">
        {/* Success Banner */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 flex items-start gap-4">
          <Heart className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-green-700">Muito obrigado pelo seu tempo e dedicação ao preencher esse formulário de seleção!</p>
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 flex items-start gap-4">
          <MessageCircleHeart className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-blue-700">
              Entre 2 a  5 dias úteis, entraremos em contato através do seu whatsapp.. fique de olho nas mensagens!<br />
              Ao alto e ao infinito! 🚀<br /><br />
              Grande abraço.<br />
              X do Infinito
            </p>
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 flex items-start gap-4">
          <div>
            <p className="text-sm text-slate-700">
              Acesse nosso instagram, para acompanhar os lançamentos da X..
            </p>
            {/* button */}
            <div className="flex items-center justify-between p-3 mt-2 bg-pink-50 rounded-lg border border-pink-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full">
                  <Instagram className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-medium text-pink-800">Instagram</p>
                  <p className="text-sm text-pink-700">@xdoinfinito</p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={
                  () => window.open('https://www.instagram.com/xdoinfinito/', '_blank')
                }
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
              >
                <Instagram className="w-4 h-4 mr-1" />
                Seguir
              </Button>
            </div>
          </div>
        </div>

        {/* Data Sections */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={toggleAllData}
            className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-150 flex items-center justify-between transition-colors"
          >
            <h3 className="font-semibold text-gray-900">📋 Dados do Pré-Cadastro</h3>
            {isAllDataCollapsed ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {!isAllDataCollapsed && (
            <div className="p-4 space-y-4">
              {dataSections.map((section) => {
                const isCollapsed = collapsedSections[section.id];
                const hasContent = section.items.some(item => item.value);

                if (!hasContent) return null;

                return (
                  <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 flex items-center justify-between transition-colors"
                    >
                      <h3 className="font-semibold text-gray-900">{section.title}</h3>
                      {isCollapsed ? (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      )}
                    </button>

                    {!isCollapsed && (
                      <div className="p-4 space-y-3 bg-white">
                        {section.items.map((item, index) => (
                          item.value && (
                            <div key={index} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                {item.label}
                              </p>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">{item.value}</p>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </StepContainer>
  );
};

export default Step23_Finalizacao;
